<template>
    <div class="footer-contact">
        <div class="footer-contact_container">
            <h3 class="headline-xs" role="heading" aria-level="4">CONNECT WITH US</h3>
            <p class="footer-contact_paragraph paragraph-details">
                Be the first to hear about our product launches, special offers,
                and much more!
            </p>
            <form
                class="footer-contact_form"
                @submit.prevent="subscribeNewsletter"
            >
                <label
                    v-if="form.subscribe_method == 'email'"
                    for="emailInput"
                    class="footer-contact_label"
                    >Your Email</label
                >
                <input
                    v-if="form.subscribe_method == 'email'"
                    v-model="form.email"
                    type="email"
                    :aria-required="true"
                    id="emailInput"
                    required
                    name="email"
                    placeholder="Your Email"
                    aria-label="Your Email"
                    class="footer-contact_input paragraph-details"
                />

                <label
                    v-if="form.subscribe_method == 'sms'"
                    for="phoneInput"
                    class="footer-contact_label"
                    >Your Phone</label
                >
                <input
                    v-if="form.subscribe_method == 'sms'"
                    v-model="form.phone"
                    :aria-required="true"
                    type="tel"
                    required
                    name="phone"
                    placeholder="Your Phone"
                    aria-label="Your Phone"
                    class="footer-contact_input paragraph-details"
                />

                <button type="submit" class="footer-contact_button">
                    Submit
                </button>
            </form>
            <div class="footer-contact_sms_message" v-if="!isDealer">
                <SignUpIcon alt="SMS icon" />
                <a
                    id="sms_email"
                    href="#"
                    @click.prevent="smsSignup()"
                    class="headline-xs"
                    variant="secondary"
                    role="button"
                >
                    Sign Up for SMS
                </a>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { ref, computed, watch, inject } from "vue";;
    import { object, string} from 'yup';
    import { useForm } from "@/helpers/composables/useForm";
    import { useFetch } from "@/helpers/composables/useFetch";
    import { useSettingsStore } from '@/store/settings';
    import NProgress from "nprogress";
    import SignUpIcon from "@icons/sing_up.svg";
    import { storeToRefs } from "pinia";
    import { useRothcoBreakpoints } from "@/helpers/composables/useBreakpoints";

    let settingsStore = useSettingsStore();
    let { setNotificationMessage } = settingsStore;
    let { is_dealer } = storeToRefs(settingsStore);
    let route = inject('route');
    const form = useForm({
        useInertia: false,
        schema: object({
            subscribe_method: string().default('email'),
            email: string()
                    .email('Must be a valid email')
                    .when('subscribe_method', {
                        is: (val) => val == "email",
                        then: (schema) =>
                            schema.required("The email field is required"),
                        otherwise: (schema) => schema.notRequired()
                    }),
            phone: string()
                    .when('subscribe_method', {
                        is: (val) => val == "sms",
                        then: (schema) =>
                            schema.required("The phone field is required"),
                        otherwise: (schema) => schema.notRequired()
                    })
            })
    });

    let isDealer = computed(()=>{
        return is_dealer.value;
    });

    let subscriptionLinkText = computed(() => {
        if (form.subscribe_method == "sms") {
            return "Sign up for Email";
        }
        return "Sign up for SMS";
    });

    let subscriptionSuccessMessage = computed(() => {
        return `Your ${form.subscribe_method == 'sms' ? 'phone number' : 'email'} has been added to the newsletter subscription.`;
    });

    const toggleSubscriptionMethod = () => {
        form.subscribe_method =
            form.subscribe_method === "email" ? "sms" : "email";
    };

    const smsSignup = () => {
        let breakpoints = useRothcoBreakpoints();
        let signUpUnit = breakpoints.smaller('tablet').value ? import.meta.env.VITE_ATTENTIVE_SIGN_UP_UNIT_MOBILE : import.meta.env.VITE_ATTENTIVE_SIGN_UP_UNIT_DESKTOP;
        window.__attentive?.trigger?.(null, null, null, +signUpUnit);
    };

    const subscribeNewsletterLoading = ref(false);
    const subscribeNewsletter = async () => {

        if(Object.keys(form.errors).length > 0){
            return;
        }

        const { onFetchResponse, onFetchError } = useFetch(
            route("endpoint.campaign.subscribe"),
            {
                beforeFetch: (ctx) => {
                    let { cancel } = ctx;
                    if(subscribeNewsletterLoading.value){
                        cancel();
                    }
                    NProgress.start();
                    subscribeNewsletterLoading.value = true;
                    
                    ctx.options.headers = {
                        ...ctx.options.headers,
                        'Accept': 'application/json',
                    }
                    return ctx;
                },
                onFetchError(ctx) {
                    subscribeNewsletterLoading.value = false;
                    if (NProgress.isStarted()) {
                        NProgress.done();
                    }
                    
                    setNotificationMessage({
                        message: "Failed to subscribe, Please contact Rothco support",
                        type: 'error'
                    });
                },
                afterFetch: async (ctx) => {
                    subscribeNewsletterLoading.value = false;
                    if (NProgress.isStarted()) {
                        NProgress.done();
                    }
                    if (ctx.response.status === 200) {
                        setNotificationMessage({
                            message: subscriptionSuccessMessage.value,
                            type: 'success'
                        });

                    } else {
                        setNotificationMessage({
                            message: "Failed to subscribe, Please contact Rothco support",
                            type: 'error'
                        });
                    }

                    return ctx;
                },

            }
        )
            .post({
                campaign: form.subscribe_method == 'email' ? {
                    email: form.email,
                    method: form.subscribe_method,
                    tag: "Landing Page"
                }: {
                    phone_number: form.phone,
                    method: form.subscribe_method,
                    tag: "Landing Page"
                }
            })
            .json();
          
    };

    watch(()=>isDealer.value, (newVal)=>{
        if(newVal){
            form.phone = null;
            form.subscribe_method = 'email';
        }
    });
</script>
